//select document
import React, { useEffect, useState } from "react"; // Import react hooks
import { Button, Modal, Table, Row, Grid } from "antd"; // Import antd components
import { Formik, Form } from "formik"; // Import formik components
import { EditOutlined, DeleteOutlined } from "@ant-design/icons"; // Import ant design icons
import EditDocuments from "../EditDocuments/EditDocuments"; // Import edit documents component
import BulkEdit from "../BulkEdit/BulkEdit"; // Import bulk edit component
import SingleEdit from "../SingleEdit/SingleEdit"; // Import single edit component
import SearchableTableWithSorting from "Common/Components/DataTable/DataTableWithSorting"; // Import SearchableTableWithSorting component
import { useSelector } from "react-redux"; // react-redux hooks import
import { getModuleAccess } from "store/slices/moduleAccessSlice"; // Import moduleAccessSlice from redux
import ActionModel from "../ActionModel/ActionModel"; // Import ActionModel component
import { fetchData } from "../commonFunction"; // Import fetchData function
import { listItemsTypesSearch, listProjectsSearch } from "utils/RESTApi"; // Import RESTApi
import DeleteDocument from "../DeleteDocument/DeleteDocument"; // Import DeleteDocument component
import { CommonError } from "utils/Helper/CommonError"; // Import CommonError
import {
  documentsByOrganizationIDCustom,
  searchDocumentsCustom,
} from "utils/Actions"; // Import graphql queries

export default function SelectDocuments(props) {
  const {
    setSelectDoc,
    selectDoc,
    uploadedFiles,
    userOrganizationId,
    currentIdToken,
    treeData,
    folderArray,
    handleDocCustom = () => {},
    handleCustomForm = () => {},
    handleAssociateData = () => {},
  } = props; // Destructure props

  const { hasReadAccess, hasDeleteAccess, hasUpdateAccess } = useSelector(
    (state) => getModuleAccess(state, "Documents")
  );

  // state variables :: begin
  const [documentEditModal, setDocumentEditModal] = useState(false);
  const [singleEdit, setSingleEdit] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState([]);
  const [uploadedData, setUploadedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectList, setProjectList] = useState([]);
  const [itemTypeList, setItemTypeList] = useState([]);
  const [nextToken, setNextToken] = useState();
  const [documentId, setDocumentId] = useState("");
  const [document, setDocument] = useState([]);
  const [deleteDoc, setDeleteDoc] = useState(false);
  // state variables :: end

  // [uploaded file in state] :: begin
  useEffect(() => {
    const uniqueArray = uploadedFiles.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id)
    );
    setUploadedData([...uniqueArray]);
  }, [uploadedFiles]);
  // [uploaded file in state] :: end

  // [fetch project and itemType data] :: begin
  useEffect(() => {
    fetchData(
      userOrganizationId,
      listProjectsSearch,
      setLoading,
      setProjectList,
      setItemTypeList,
      setNextToken
    );
    fetchData(
      userOrganizationId,
      listItemsTypesSearch,
      setLoading,
      setProjectList,
      setItemTypeList,
      setNextToken
    );
  }, []);
  // [fetch project and itemType data] :: end

  const { useBreakpoint } = Grid; // Import Grid component from antd
  const screens = useBreakpoint(); // Get the screens breakpoints

  // document columns :: begin
  const columns = [
    {
      title: "File Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Action",
      dataIndex: "Action",
      key: "action",
      render: (_, value) => {
        return (
          <>
            <Row gutter={[24, 0]}>
              <Button
                key={`edit-${value?.id}`} // Add a unique key
                type="link"
                className="p-0 mr-20"
                onClick={() => {
                  setDocumentId(value?.id);
                  setDocument([
                    ...uploadedData.filter((item) => item?.id === value?.id),
                  ]);
                  setDocumentEditModal(true);
                }}
              >
                <EditOutlined />
              </Button>
              <Button
                key={`delete-${value?.id}`} // Add a unique key
                onClick={() => {
                  setDeleteDoc(true);
                  setDocumentId(value?.id);
                  setDocument([
                    ...uploadedData.filter((item) => item?.id === value?.id),
                  ]);
                }}
                type="link"
                className="p-0 mr-20 redBtn"
              >
                <DeleteOutlined />
              </Button>
            </Row>
          </>
        );
      },
    },
  ];
  // document columns :: end

  useEffect(() => {
    if (uploadedData?.length === 0) {
      setSelectDoc(false);
    }
  }, [uploadedData]);

  // selected rows data to attach item :: begin
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedDocument(selectedRows);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      name: record.name,
    }),
  };
  // selected rows data to attach item :: end

  const customDeleteFunction = async (val, docData) => {
    try {
      // Create a copy of the current uploaded documents
      const documentsAdded = [...uploadedData];

      // Use Promise.all to handle multiple document searches concurrently
      const updatedDocuments = await Promise.all(
        documentsAdded.map(async (item) => {
          if (item?.id) {
            let filter = {
              id: { eq: item.id },
              organizationID: { eq: userOrganizationId },
              isDeleted: { ne: true },
              isFromMobile: { ne: true },
            };

            const sort = {};

            try {
              const response = await searchDocumentsCustom(
                filter,
                sort,
                9999,
                null,
                currentIdToken
              );

              // Return the first item from the response (assuming it's the updated document)
              return response?.items?.[0] || item;
            } catch (error) {
              console.error(`Error fetching document ${item.id}:`, error);
              return item; // Return original item if fetch fails
            }
          }
          return item;
        })
      );
      // Remove any potential duplicate documents by using a Set based on ID
      if (val === "delete") {
        //removed deleted document from state
        setUploadedData(uploadedData.filter((doc) => doc.id !== docData[0].id));
      } else {
        documentsAdded.map((doc) => {
          if (doc.id === docData[0].id) {
            doc.name = val?.name;
            // doc = docData[0];
          }
        });
        // Update the state with the new documents
        setUploadedData(documentsAdded);
      }

      // Reset states
      setDocumentId("");
      setNextToken(null);
      setDocumentEditModal(false);
    } catch (error) {
      CommonError(error);
    } finally {
      setDeleteDoc(false);
    }
  };

  // reset states :: begin
  const handleReset = () => {
    setDocumentId("");
    setDocumentEditModal(false);
    setDeleteDoc(false);
    setSelectDoc(false);
  };
  // reset states :: end

  useEffect(() => {
    if (!selectDoc) {
      handleDocCustom("Close");
    } else {
      setSelectedDocument([]);
    }
  }, [selectDoc]);

  // UI component rendering :: begin
  return (
    <>
      <Modal
        title="Select Document"
        width={497}
        centered
        className="modal"
        maskClosable={true}
        open={selectDoc}
        onCancel={() => {
          setSelectDoc(false);
        }}
        footer={[
          <Button
            type="primary"
            onClick={() => {
              setSelectDoc(false);
              setOpen(true);
            }}
            disabled={selectedDocument?.length > 1 ? false : true}
          >
            Multiple Associate
          </Button>,
          <Button
            type="primary"
            onClick={() => {
              setSelectDoc(false);
              handleAssociateData(selectedDocument);
            }}
            disabled={selectedDocument?.length >= 1 ? false : true}
          >
            Single Associate
          </Button>,
        ]}
      >
        <Formik>
          <Form id="editDocument" className="upload-doc">
            <div className="mb-10">
              <SearchableTableWithSorting
                className={"overlay-table"}
                data={uploadedData}
                columns={columns}
                screens={screens}
                rowSelection={rowSelection}
                hideSelection={false}
                rowKey={(record) => record.id} // Ensure unique key
              />
            </div>
          </Form>
        </Formik>
      </Modal>

      {/* BulkEdit modal component */}
      <BulkEdit
        open={open}
        screens={screens}
        setOpen={() => setOpen()}
        selectedDocuments={selectedDocument}
        projectData={projectList}
        itemTypeData={itemTypeList}
        organizationID={userOrganizationId}
        currentIdToken={currentIdToken}
        handleCustomBulkEdit={(data) => handleCustomForm(data)}
      />

      {/* <SingleEdit
        singleEdit={singleEdit}
        setSingleEdit={() => setSingleEdit()}
        projectData={projectList}
        itemTypeData={itemTypeList}
        documentData={selectedDocument}
        organizationID={userOrganizationId}
        currentIdToken={currentIdToken}
        activeForm={0}
        handleCustomForm={(data) => handleCustomForm(data)}
      /> */}

      {/* Document edit modal component */}
      <EditDocuments
        documentEditModal={documentEditModal}
        setDocumentEditModal={setDocumentEditModal}
        currentIdToken={currentIdToken}
        handleCustomSubmit={(val) => customDeleteFunction(val, document)}
        treeData={treeData}
        folderArray={folderArray}
        documentToEdit={document}
      />

      {/* Delete document modal component */}
      <DeleteDocument
        deleteDoc={deleteDoc}
        setDeleteDoc={() => setDeleteDoc()}
        // handleDelete={() => {
        //   uploadedData?.length > 1 ? customDeleteFunction() : handleReset();
        // }}
        onDeleteSuccess={() => {
          uploadedData?.length === 1
            ? handleReset()
            : customDeleteFunction("delete", document);
        }}
        documentId={documentId}
        currentIdToken={currentIdToken}
        userOrganizationId={userOrganizationId}
        documentToDelete={document}
        multiDelete={false}
        deleteAttachments={[]}
      />
    </>
  );
  // UI component rendering :: end
}
